import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { useAuth } from '../../contexts/AuthContext';

const AuthenticationLogin = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: ''
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [isRegisterMode, setIsRegisterMode] = useState(false);
  
  const { signIn, signUp, signInWithGoogle, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
      navigate('/dashboard-overview');
    }
  }, [user, navigate]);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    if (error) setError(null);
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      if (isRegisterMode) {
        const { data, error: signUpError } = await signUp(formData?.email, formData?.password, { name: formData?.name });
        if (signUpError) {
          setError(signUpError?.message || 'Erro ao cadastrar');
        } else if (data?.user) {
          navigate('/dashboard-overview');
        }
      } else {
        const { data, error: signInError } = await signIn(formData?.email, formData?.password);
        if (signInError) {
          setError(signInError?.message || 'Erro ao fazer login');
        } else if (data?.user) {
          navigate('/dashboard-overview');
        }
      }
    } catch (error) {
      setError('Erro inesperado. Tente novamente.');
      console.error('Login error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const { data, error: oauthError } = await signInWithGoogle();
      if (oauthError) {
        setError(oauthError?.message || 'Erro ao entrar com Google');
      } else if (data?.url) {
        // Supabase retorna URL para redirecionar em apps SPA
        window.location.href = data.url;
      }
    } catch (err) {
      setError('Erro inesperado no OAuth. Tente novamente.');
      console.error('OAuth login error:', err);
    } finally {
      // Em fluxo OAuth real, a página será redirecionada; mantemos loading até a troca de rota
      setIsLoading(false);
    }
  };

  // Removido: credenciais de demonstração

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        {/* Glassmorphism Card */}
        <div className="bg-card/80 backdrop-blur-xl border border-border/50 rounded-2xl p-8 shadow-2xl">
          {/* Logo and Header */}
          <div className="text-center mb-8">
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.3, delay: 0.1 }}
              className="flex justify-center mb-4"
            >
              <div className="w-16 h-16 bg-gradient-to-br from-primary to-secondary rounded-2xl flex items-center justify-center shadow-lg">
                <svg 
                  viewBox="0 0 24 24" 
                  className="w-8 h-8 text-white"
                  fill="currentColor"
                >
                  <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/>
                </svg>
              </div>
            </motion.div>
            <h1 className="text-2xl font-bold text-foreground mb-2">
              Bem-vindo ao FioGo
            </h1>
            <p className="text-muted-foreground text-sm">
              Calculadora avançada de custos para impressão 3D
            </p>
          </div>

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <motion.div
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                className="bg-destructive/10 border border-destructive/20 rounded-lg p-3 flex items-center space-x-2"
              >
                <Icon name="AlertCircle" size={16} className="text-destructive" />
                <span className="text-destructive text-sm">{error}</span>
              </motion.div>
            )}

            <div className="space-y-4">
              {isRegisterMode && (
                <Input
                  type="text"
                  label="Nome"
                  placeholder="Seu nome"
                  value={formData?.name}
                  onChange={(e) => handleInputChange('name', e?.target?.value)}
                  required
                  className="bg-input border-border"
                />
              )}
              <Input
                type="email"
                label="Email"
                placeholder="seu@email.com"
                value={formData?.email}
                onChange={(e) => handleInputChange('email', e?.target?.value)}
                required
                className="bg-input border-border"
              />

              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  label="Senha"
                  placeholder="••••••••"
                  value={formData?.password}
                  onChange={(e) => handleInputChange('password', e?.target?.value)}
                  required
                  className="bg-input border-border pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-8 text-muted-foreground hover:text-foreground transition-colors"
                >
                  <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={16} />
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={isLoading || !formData?.email || !formData?.password}
              className="w-full bg-gradient-to-r from-primary to-secondary hover:from-primary/90 hover:to-secondary/90 shadow-lg"
            >
              {isLoading ? (
                <div className="flex items-center space-x-2">
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  <span>{isRegisterMode ? 'Cadastrando...' : 'Entrando...'}</span>
                </div>
              ) : (
                <div className="flex items-center space-x-2">
                  <Icon name={isRegisterMode ? 'UserPlus' : 'LogIn'} size={16} />
                  <span>{isRegisterMode ? 'Cadastrar' : 'Entrar'}</span>
                </div>
              )}
            </Button>
            <div className="relative my-2">
              <div className="flex items-center">
                <div className="flex-1 h-px bg-muted" />
                <span className="mx-3 text-xs text-muted-foreground">ou</span>
                <div className="flex-1 h-px bg-muted" />
              </div>
            </div>
            <Button
              type="button"
              onClick={handleGoogleLogin}
              disabled={isLoading}
              variant="outline"
              className="w-full hover:bg-muted"
            >
              <div className="flex items-center space-x-2">
                <Icon name="Chrome" size={16} />
                <span>Entrar com Google</span>
              </div>
            </Button>
            <div className="flex items-center justify-between">
              <button
                type="button"
                className="text-xs text-primary hover:text-primary/80"
                onClick={() => { setIsRegisterMode(!isRegisterMode); setError(null); }}
              >
                {isRegisterMode ? 'Já tenho conta → Entrar' : 'Não tenho conta → Cadastrar'}
              </button>
            </div>
          </form>

          {/* Removido: bloco de credenciais de demonstração */}

          {/* Footer removido a pedido: links de acesso sem conta */}
        </div>

        {/* Additional Info */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.3 }}
          className="text-center mt-6"
        >
          <p className="text-xs text-muted-foreground">
            Sistema de precificação profissional para impressão 3D
          </p>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default AuthenticationLogin;